import React, { useState, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { SelectBox } from 'devextreme-react/select-box'
import { Form, Formik } from 'formik'
import Div from '@jumbo/shared/Div'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import JumboAppTextField from '@jumbo/components/JumboAppTextField'
import LoadingButton from '@mui/lab/LoadingButton'
import SearchIcon from '@mui/icons-material/Search'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { Checkbox, FormControlLabel, FormGroup, useMediaQuery } from '@mui/material'
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded'
import DataSource from 'devextreme/data/data_source'
import CustomStore from 'devextreme/data/custom_store'
import { useSnackbar } from 'notistack'
import utilServices from 'app/services/util-services'
import classTypeServices from 'app/services/pages/settings/definitions/classTypeServices/classTypeServices'
import teacherServices from 'app/services/pages/security/teacherServices/teacherServices'
import { useJumboTheme } from '@jumbo/hooks'

const ClassGridFilter = ({ termYear, mutation, display, filterIconColor }) => {
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()
    const [filter, setFilter] = useState({ classTypeId: null, teacherId: null })
    const [deletedRecordsSelection, setDeletedRecordsSelection] = useState(false)
    const { theme } = useJumboTheme()
    const sm = useMediaQuery(theme.breakpoints.down('sm'))

    function handleError(error) {
        if (error.status === '401') {
            navigate('/profile/signout')
        } else if (error.detail) {
            enqueueSnackbar(error.detail, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        } else {
            enqueueSnackbar(error, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        }
    }

    const toFullName = useCallback((data) => {
        return data ? data.firstName + ' ' + data.lastName : ''
    }, [])

    const teacherLookupList = useCallback(async (searchTerm) => {
        try {
            return await teacherServices.getTeacherLookupList({ firstName: searchTerm })
        } catch (error) {
            handleError(error)
        }
    }, [])

    const teacherLookupById = useCallback(async (key) => {
        try {
            return await teacherServices.getTeacherById(key)
        } catch (error) {
            handleError(error)
        }
    }, [])

    const teacherLookupsStore = useMemo(
        () =>
            new DataSource({
                store: new CustomStore({
                    key: 'id',
                    load: async function (loadOptions) {
                        let searchTerm = ''
                        if (loadOptions.searchValue) {
                            searchTerm = loadOptions.searchValue
                        }
                        return await teacherLookupList(searchTerm)
                    },
                    byKey: function (key) {
                        if (key === '') return utilServices.emptyList()

                        return teacherLookupById(key)
                    },
                    errorHandler: function (e) {
                        console.log(e)
                        return utilServices.emptyList()
                    },
                }),
                reshapeOnPush: true,
            }),
        []
    )

    const classTypeLookupList = useCallback(
        async (searchTerm) => {
            try {
                return await classTypeServices.getClassTypeLookupList({ classTypeName: searchTerm })
            } catch (error) {
                handleError(error)
            }
        },
        [classTypeServices]
    )

    const classTypeLookupById = useCallback(
        async (key) => {
            try {
                return await classTypeServices.getClassTypeLookupById(key)
            } catch (error) {
                handleError(error)
            }
        },
        [classTypeServices]
    )

    const classTypeStore = useMemo(
        () =>
            new DataSource({
                store: new CustomStore({
                    key: 'id',
                    load: async function (loadOptions) {
                        let searchTerm = ''
                        if (loadOptions.searchValue) {
                            searchTerm = loadOptions.searchValue
                        }
                        return await classTypeLookupList(searchTerm)
                    },
                    byKey: function (key) {
                        if (key === '') return utilServices.emptyList()

                        return classTypeLookupById(key)
                    },
                    errorHandler: function (e) {
                        console.log(e)
                        return utilServices.emptyList()
                    },
                }),
                reshapeOnPush: true,
            }),
        []
    )

    let initialValues = {
        className: '',
    }
    const onFormSubmit = (className) => {
        mutation.mutate({
            termYear: termYear,
            className: className,
            sort: 'className',
            isDescending: false,
            ...filter,
            includeDeletedRecords: deletedRecordsSelection,
        })
    }

    const onFilterSubmit = async (data, { setSubmitting }) => {
        setSubmitting(true)
        await onFormSubmit(data.className)
        setSubmitting(false)
    }
    const handleClear = () => {
        filterIconColor(false)
        setFilter({ classTypeId: null, teacherId: null })
        setDeletedRecordsSelection(false)
    }

    const onClassTypeSelectionChanged = (value) => {
        setFilter({ ...filter, classTypeId: value?.id })
    }

    const onTeacherSelectionChanged = (value) => {
        setFilter({ ...filter, teacherId: value?.id })
    }

    return (
        <Formik
            validateOnChange={true}
            initialValues={initialValues}
            onSubmit={onFilterSubmit}
            onReset={onFilterSubmit}>
            {({ isSubmitting }) => (
                <Form noValidate autoComplete='off'>
                    <JumboCardQuick noWrapper sx={{ display: display, borderRadius: 0 }}>
                        <Div
                            className='grid-filter-parent'
                            style={{ display: 'flex', margin: '0 1.5% 1.5% 1.5%', flexWrap: 'wrap' }}>
                            <Div sx={{ mr: 3, mt: 1.5 }}>
                                <Div>
                                    <Typography>Class Name</Typography>
                                </Div>
                                <Div sx={{ mt: 0.5 }}>
                                    <JumboAppTextField style={{ width: 220 }} size='small' name='className' />
                                </Div>
                            </Div>
                            <Div sx={{ mr: 3, mt: 1.5 }}>
                                <Div>
                                    <Typography>Class Type</Typography>
                                </Div>
                                <Div sx={{ mt: 0.5 }}>
                                    <SelectBox
                                        style={{ width: 220 }}
                                        dataSource={classTypeStore}
                                        displayExpr={'classTypeName'}
                                        value={filter.classTypeId}
                                        searchEnabled={true}
                                        onValueChange={onClassTypeSelectionChanged}
                                        placeholder='All'
                                        showClearButton={true}
                                    />
                                </Div>
                            </Div>
                            <Div className='report-filter-checkbox'>
                                <Div sx={{ mr: 3, mt: 1.5 }}>
                                    <Div>
                                        <Typography>Teacher</Typography>
                                    </Div>
                                    <Div sx={{ mt: 0.5 }}>
                                        <SelectBox
                                            style={{ width: 220 }}
                                            dataSource={teacherLookupsStore}
                                            itemTemplate={toFullName}
                                            displayExpr={toFullName}
                                            value={filter.teacherId}
                                            searchEnabled={true}
                                            onValueChange={onTeacherSelectionChanged}
                                            placeholder='All'
                                            showClearButton={true}
                                        />
                                    </Div>
                                </Div>
                                <Div sx={{ mr: 3, mt: 3.5 }}>
                                    <Div sx={{ mt: 0.5 }}>
                                        <FormGroup>
                                            <FormControlLabel
                                                control={<Checkbox />}
                                                type='checkbox'
                                                checked={deletedRecordsSelection}
                                                onChange={(e) => setDeletedRecordsSelection(!deletedRecordsSelection)}
                                                label='Show All'
                                            />
                                        </FormGroup>
                                    </Div>
                                </Div>
                            </Div>
                            <Div className='grid-filter-Button' sx={{ mt: sm ? 3 : 5 }}>
                                <Stack direction={'row'} spacing={1} sx={{}}>
                                    <LoadingButton
                                        size={'small'}
                                        type='submit'
                                        onClick={() => filterIconColor(true)}
                                        variant={'contained'}
                                        loading={isSubmitting || mutation.isLoading}>
                                        <SearchIcon sx={{ fontSize: 18 }} /> Search
                                    </LoadingButton>
                                    <LoadingButton
                                        size={'small'}
                                        type='reset'
                                        onClick={handleClear}
                                        variant={'contained'}>
                                        <AutorenewRoundedIcon sx={{ fontSize: 18 }} /> Clear
                                    </LoadingButton>
                                </Stack>
                            </Div>
                        </Div>
                    </JumboCardQuick>
                </Form>
            )}
        </Formik>
    )
}

export default ClassGridFilter
